import { useEffect, useRef, useState } from "react";
import { useMatch, useNavigate, useParams } from "react-router-dom";
import jsQR from "jsqr";
import {
  Box,
  Card,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import eyeFill from "@iconify/icons-eva/eye-fill";
import eyeOffFill from "@iconify/icons-eva/eye-off-fill";
import imageOutline from "@iconify/icons-eva/image-outline";
import { Form, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { LoadingButton } from "@mui/lab";
import { Loader } from "../Loader";
import { useQuery } from "@tanstack/react-query";
import { fetchWithError } from "src/react-query/fetchWithError";
import { queryKeys } from "src/react-query/queryKeys";
import { selector } from "src/react-query/select";
import { paymentGatewayDetailsUrl, paymentGatewayDetailByIdUrl } from "src/react-query/endPoints";
import { useMutate } from "src/hooks/useFetch.hook";
import { alertSlice } from "src/store/alert/alert.slice";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { getPaymentGatewayDefaultValues } from "./PaymentGateway.constants";
import { is } from "src/util/is";
import Countries from "src/data/countries.json";

const validationSchema = Yup.object({
  payment_gateway_id: Yup.number()
    .required("Payment Gateway ID is required")
    .typeError("Payment Gateway ID must be a number"),
  key_id: Yup.string().required("Key ID is required"),
  key_secret: Yup.string().required("Key Secret is required"),
  webhook_secret: Yup.string(),
  currency: Yup.string(),
  country: Yup.string(),
  gateway_type: Yup.string(),
  name: Yup.string(),
  domain: Yup.string(),
  upi_url: Yup.string(),
});

export const PaymentGatewayForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const isCreate = useMatch("/payment-gateways/new");
  const { mutate, isLoading } = useMutate("paymentGatewayDetails");
  const [showKeySecret, setShowKeySecret] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);

  const { data: paymentGateway, isFetching } = useQuery({
    queryKey: queryKeys.paymentGatewayDetail(id),
    queryFn: fetchWithError,
    select: selector,
    enabled: !isCreate && !is.empty(id),
    // The edit form must always show the current DB values. The global config
    // keeps queries fresh for 5 minutes, which would otherwise serve a stale
    // cached record (e.g. after a previous edit), so force a fresh fetch here.
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: "always",
  });

  // The gateway providers this record can point at. payment_gateway_id is a
  // foreign key into cms_payment_gateway, so a typed-in number that matches no
  // row failed at save time as an opaque constraint error — pick from the list.
  const { data: gatewayOptions = [], isFetching: isFetchingGateways } = useQuery({
    queryKey: queryKeys.paymentGatewayMasterList(),
    queryFn: fetchWithError,
    select: selector,
  });

  const formik = useFormik({
    initialValues: getPaymentGatewayDefaultValues(),
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      const url = isCreate
        ? paymentGatewayDetailsUrl
        : paymentGatewayDetailByIdUrl(id);
      const method = isCreate ? "POST" : "PUT";

      mutate(
        {
          url,
          method,
          body: values,
        },
        {
          onSuccess: (response) => {
            dispatch(
              alertSlice.actions.setAlert({
                open: true,
                message: isCreate
                  ? "Payment gateway created successfully"
                  : "Payment gateway updated successfully",
                severity: "success",
              })
            );
            queryClient.invalidateQueries(queryKeys.paymentGatewayDetails());
            // Also invalidate this record's detail cache so reopening edit
            // shows the freshly saved values, not the stale cached ones.
            if (!isCreate && !is.empty(id)) {
              queryClient.invalidateQueries(queryKeys.paymentGatewayDetail(id));
            }
            navigate("/payment-gateways");
            setSubmitting(false);
          },
          onError: (error) => {
            // The API always says why: 403 for the GP tenant check or a missing
            // PaymentGateway grant, 400 for missing fields or a duplicate
            // key_id. Read that message first — error.message is only axios's
            // "Request failed with status code N", and the generic fallback hid
            // the reason entirely. This handler runs after the one in useMutate,
            // so whatever it dispatches is what the user ends up seeing.
            const message =
              error?.response?.data?.message ||
              error?.message ||
              "Failed to save payment gateway";
            dispatch(
              alertSlice.actions.setAlert({
                open: true,
                message,
                severity: "error",
              })
            );
            setSubmitting(false);
          },
        }
      );
    },
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
  } = formik;

  // Upload a UPI QR image, decode it in the browser, and auto-fill the UPI URL.
  const qrInputRef = useRef(null);

  const notify = (message, severity) =>
    dispatch(alertSlice.actions.setAlert({ open: true, message, severity }));

  const handleQrUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      notify("Please upload an image file", "error");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          const { data, width, height } = ctx.getImageData(
            0,
            0,
            canvas.width,
            canvas.height
          );
          const code = jsQR(data, width, height);
          if (code?.data) {
            setFieldValue("upi_url", code.data.trim());
            notify("UPI URL extracted from QR", "success");
          } else {
            notify("No QR code detected in the image", "error");
          }
        } catch (err) {
          notify("Could not read the QR image", "error");
        }
      };
      img.onerror = () => notify("Invalid image file", "error");
      img.src = reader.result;
    };
    reader.onerror = () => notify("Could not read the file", "error");
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (paymentGateway && !isCreate && !isFetching) {
      formik.setValues({
        payment_gateway_id: paymentGateway.payment_gateway_id || "",
        key_id: paymentGateway.key_id || "",
        key_secret: paymentGateway.key_secret || "",
        webhook_secret: paymentGateway.webhook_secret || "",
        currency: paymentGateway.currency || "INR",
        country: paymentGateway.country || "IN",
        gateway_type: paymentGateway.gateway_type || "",
        name: paymentGateway.name || "",
        domain: paymentGateway.domain || "",
        upi_url: paymentGateway.upi_url || "",
      });
    }
  }, [paymentGateway, isCreate, isFetching]);

  if (isFetching && !isCreate) return <Loader />;

  return (
    <Card sx={{ p: 3 }}>
      <FormikProvider value={formik}>
        <Form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6">Payment Gateway Details</Typography>
            </Grid>

            {/* Payment Gateway */}
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                error={Boolean(touched.payment_gateway_id && errors.payment_gateway_id)}
              >
                <InputLabel>Payment Gateway</InputLabel>
                <Select
                  {...getFieldProps("payment_gateway_id")}
                  value={values.payment_gateway_id ?? ""}
                  label="Payment Gateway"
                  disabled={isFetchingGateways}
                >
                  {(Array.isArray(gatewayOptions) ? gatewayOptions : []).map(
                    (gateway) => (
                      <MenuItem
                        key={gateway.paymentGatewayId}
                        value={gateway.paymentGatewayId}
                      >
                        {gateway.paymentGateway}
                        {gateway.currency ? ` (${gateway.currency})` : ""}
                      </MenuItem>
                    )
                  )}
                </Select>
                <FormHelperText>
                  {(touched.payment_gateway_id && errors.payment_gateway_id) ||
                    "The provider this configuration belongs to"}
                </FormHelperText>
              </FormControl>
            </Grid>

            {/* Gateway Type */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Gateway Type"
                {...getFieldProps("gateway_type")}
                error={Boolean(touched.gateway_type && errors.gateway_type)}
                helperText={touched.gateway_type && errors.gateway_type}
                placeholder="e.g., EASEBUZZ, STRIPE"
              />
            </Grid>

            {/* Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                {...getFieldProps("name")}
                error={Boolean(touched.name && errors.name)}
                helperText={touched.name && errors.name}
              />
            </Grid>

            {/* Domain */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Domain"
                {...getFieldProps("domain")}
                error={Boolean(touched.domain && errors.domain)}
                helperText={touched.domain && errors.domain}
              />
            </Grid>

            {/* UPI URL */}
            <Grid item xs={12} md={6}>
              <input
                ref={qrInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleQrUpload}
              />
              <TextField
                fullWidth
                label="UPI URL"
                placeholder="upi://pay?pa=paytm.s1gh6sr@pty&pn=Paytm"
                {...getFieldProps("upi_url")}
                error={Boolean(touched.upi_url && errors.upi_url)}
                helperText={
                  (touched.upi_url && errors.upi_url) ||
                  "Or upload a UPI QR to auto-fill this field"
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        title="Upload UPI QR to extract the URL"
                        onClick={() => qrInputRef.current?.click()}
                      >
                        <Icon icon={imageOutline} width={22} height={22} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Key ID */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Key ID"
                {...getFieldProps("key_id")}
                error={Boolean(touched.key_id && errors.key_id)}
                helperText={touched.key_id && errors.key_id}
                required
              />
            </Grid>

            {/* Key Secret */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Key Secret"
                type={showKeySecret ? "text" : "password"}
                {...getFieldProps("key_secret")}
                error={Boolean(touched.key_secret && errors.key_secret)}
                helperText={touched.key_secret && errors.key_secret}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowKeySecret((prev) => !prev)}
                        edge="end"
                        sx={{
                          color: 'text.secondary',
                          '&:hover': { color: 'primary.main' }
                        }}
                      >
                        <Icon icon={showKeySecret ? eyeFill : eyeOffFill} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Webhook Secret */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Webhook Secret"
                type={showWebhookSecret ? "text" : "password"}
                {...getFieldProps("webhook_secret")}
                error={Boolean(touched.webhook_secret && errors.webhook_secret)}
                helperText={touched.webhook_secret && errors.webhook_secret}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowWebhookSecret((prev) => !prev)}
                        edge="end"
                        sx={{
                          color: 'text.secondary',
                          '&:hover': { color: 'primary.main' }
                        }}
                      >
                        <Icon icon={showWebhookSecret ? eyeFill : eyeOffFill} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Currency */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  {...getFieldProps("currency")}
                  value={values.currency || "INR"}
                  label="Currency"
                >
                  <MenuItem value="INR">INR - Indian Rupee</MenuItem>
                  <MenuItem value="USD">USD - US Dollar</MenuItem>
                  <MenuItem value="EUR">EUR - Euro</MenuItem>
                  <MenuItem value="GBP">GBP - British Pound</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Country */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Country</InputLabel>
                <Select
                  {...getFieldProps("country")}
                  value={values.country || "IN"}
                  label="Country"
                >
                  {Countries.map((country) => (
                    <MenuItem key={country.code} value={country.code}>
                      {country.name} ({country.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <LoadingButton
                  size="large"
                  type="submit"
                  variant="contained"
                  loading={isSubmitting || isLoading}
                >
                  {isCreate ? "Create" : "Update"}
                </LoadingButton>
                <LoadingButton
                  size="large"
                  type="button"
                  variant="outlined"
                  onClick={() => navigate("/payment-gateways")}
                >
                  Cancel
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </Form>
      </FormikProvider>
    </Card>
  );
};

